import React, { useState, useEffect } from 'react';
import { Megaphone, Plus, Trash2, X, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { getAnnouncements, createAnnouncement, deleteAnnouncement } from '../../services/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await getAnnouncements();
      setAnnouncements(res.data || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      const res = await createAnnouncement(data.title, data.content, data.type);
      toast.success('Announcement created');
      setAnnouncements([res.data, ...announcements]);
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await deleteAnnouncement(id);
      toast.success('Announcement deleted');
      setAnnouncements(announcements.filter(a => a.id !== id));
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertCircle size={18} className="text-yellow-500" />;
      case 'success': return <CheckCircle size={18} className="text-green-500" />;
      default: return <Info size={18} className="text-blue-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      info: 'bg-blue-500/20 text-blue-400',
      warning: 'bg-yellow-500/20 text-yellow-400',
      success: 'bg-green-500/20 text-green-400'
    };
    return colors[type] || colors.info;
  };

  return (
    <div className="space-y-6" data-testid="admin-announcements">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-teko">Announcements</h2>
        <Button onClick={() => setShowModal(true)}>
          <Plus size={16} className="mr-2" />
          New Announcement
        </Button>
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 animate-pulse">
              <div className="h-5 w-1/3 bg-[var(--border)] rounded mb-3" />
              <div className="h-4 w-full bg-[var(--border)] rounded" />
            </div>
          ))
        ) : announcements.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-secondary)]">
            <Megaphone size={48} className="mx-auto mb-4 opacity-50" />
            <p>No announcements yet</p>
          </div>
        ) : (
          announcements.map(announcement => (
            <div key={announcement.id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
              <div className="flex items-start justify-between">
                <div className="flex gap-3">
                  {getTypeIcon(announcement.type)}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">{announcement.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getTypeBadge(announcement.type)}`}>
                        {announcement.type}
                      </span>
                      {announcement.is_active && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-[var(--text-secondary)] text-sm">{announcement.content}</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-2">
                      Created {new Date(announcement.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(announcement.id)}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <AnnouncementModal
          onClose={() => setShowModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

function AnnouncementModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ title: '', content: '', type: 'info' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onCreate(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between mb-4">
            <h2 className="text-2xl font-teko">New Announcement</h2>
            <button onClick={onClose}><X size={20} /></button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({...form, type: e.target.value})}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="success">Success</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Content</label>
              <textarea
                value={form.content}
                onChange={(e) => setForm({...form, content: e.target.value})}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg h-24 resize-none"
                required
              />
            </div>
            <Button type="submit" disabled={saving} className="w-full">
              {saving ? 'Creating...' : 'Create Announcement'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminAnnouncements;
